<template>
    <section class="space-y-6">
        <div class="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
            <SelectButton
                class="w-full"
                :model-value="travelType"
                :label="t('reservation.step2.travelType')"
                :value="travelTypeDisplayValue"
                :options="travelTypeOptions"
                isFull
                @update:model-value="emit('update:travelType', $event as 'flight' | 'train' | '')"
            />
            <FormInput
                :model-value="travelNumber"
                :label="t('reservation.step2.referenceNumber')"
                :placeholder="t('reservation.step2.referencePlaceholder')"
                @update:model-value="emit('update:travelNumber', $event)"
            />
        </div>

        <hr class="border-t-2 border-primary-dark/30" />

        <div>
            <SelectButton
                class="w-full"
                :label="t('reservation.step2.vehicleType')"
                :value="vehicleTypeDisplayValue"
                :model-value="vehicleType"
                :options="vehicleTypeOptions"
                required
                isFull
                :class="attemptedNext && !isVehicleTypeValid ? 'ring-2 ring-red-400' : ''"
                @update:model-value="emit('update:vehicleType', $event)"
            />
            <p v-if="attemptedNext && !isVehicleTypeValid" class="mt-1 text-sm text-red-600">
                {{ t('reservation.required.vehicleType') }}
            </p>
        </div>

        <!-- Equipment: baby seats / child seats / strollers / suitcases -->
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SelectButton
                class="w-full"
                :label="t('reservation.step1.babySeat')"
                :value="babySeatDisplayValue"
                :model-value="babySeatDisplayValue"
                :options="quantityOptions"
                isFull
                @update:model-value="emit('update:babySeat', Number($event))"
            />
            <SelectButton
                class="w-full"
                :label="t('reservation.step1.childSeat')"
                :value="childSeatDisplayValue"
                :model-value="childSeatDisplayValue"
                :options="quantityOptions"
                isFull
                @update:model-value="emit('update:childSeat', Number($event))"
            />
            <SelectButton
                class="w-full"
                :label="t('reservation.step1.numberOfStrollers')"
                :value="numberOfStrollersDisplayValue"
                :model-value="numberOfStrollersDisplayValue"
                :options="quantityOptions"
                isFull
                @update:model-value="emit('update:numberOfStrollers', Number($event))"
            />
            <SelectButton
                class="w-full"
                :label="t('reservation.step1.numberOfSuitcases')"
                :value="numberOfSuitcasesDisplayValue"
                :model-value="numberOfSuitcasesDisplayValue"
                :options="quantityOptions"
                isFull
                @update:model-value="emit('update:numberOfSuitcases', Number($event))"
            />
        </div>
    </section>
</template>

<script setup lang="ts">
import FormInput from '@/Components/Molecules/FormInput.vue';
import SelectButton from '@/Components/Molecules/SelectButton.vue';
import { useTranslations } from '@/composables/useTranslations';
import { computed } from 'vue';

const { t } = useTranslations();

interface Props {
    travelType: 'flight' | 'train' | '';
    travelNumber: string;
    vehicleType: string;
    vehicleTypes: string[];
    babySeat: number;
    childSeat: number;
    numberOfStrollers: number;
    numberOfSuitcases: number;
    totalPassengers: number;
    attemptedNext: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    (e: 'update:travelType', value: 'flight' | 'train' | ''): void;
    (e: 'update:travelNumber', value: string): void;
    (e: 'update:vehicleType', value: string): void;
    (e: 'update:babySeat', value: number): void;
    (e: 'update:childSeat', value: number): void;
    (e: 'update:numberOfStrollers', value: number): void;
    (e: 'update:numberOfSuitcases', value: number): void;
    (e: 'previous'): void;
    (e: 'next'): void;
}>();

const travelTypeOptions = computed(() => [
    { label: t('reservation.step2.select'), value: '' },
    { label: t('reservation.step2.flight'), value: 'flight' },
    { label: t('reservation.step2.train'), value: 'train' },
]);

const vehicleTypeOptions = computed(() => [
    { label: t('reservation.step2.select'), value: '' },
    ...props.vehicleTypes.map((vehicleType) => ({
        label: vehicleType,
        value: vehicleType,
    })),
]);

const quantityOptions = computed(() =>
    Array.from({ length: props.totalPassengers + 1 }, (_, i) => ({
        label: String(i),
        value: String(i),
    })),
);

const travelTypeDisplayValue = computed(() => {
    const selected = travelTypeOptions.value.find((option) => option.value === props.travelType);
    return selected?.label ?? t('reservation.step2.select');
});

const isVehicleTypeValid = computed(() => props.vehicleType.trim().length > 0);
const vehicleTypeDisplayValue = computed(() => {
    const selected = vehicleTypeOptions.value.find((option) => option.value === props.vehicleType);
    return selected?.label ?? t('reservation.step2.select');
});

const babySeatDisplayValue = computed(() => String(props.babySeat));
const childSeatDisplayValue = computed(() => String(props.childSeat));
const numberOfStrollersDisplayValue = computed(() => String(props.numberOfStrollers));
const numberOfSuitcasesDisplayValue = computed(() => String(props.numberOfSuitcases));
</script>
